import { Navigate, Outlet, useLocation } from "react-router-dom";

// Components
import { RouterPathMapping } from "./Router";

// Hooks
import { useAuth } from "./hooks/useAuth";
import useClaimsToast from "./hooks/useToast";

// Utils
import { UserRole } from "./utils/consts";

interface RestrictedRouteProps {
	redirect?: string;
	roles: UserRole[];
}

/**
 * A restricted route for only users of specific roles
 * @param props.redirect `string`: The route to redirect to if access id denied. Defaults to the access denied page.
 * @param props.roles `UserRole[]`: The roles allowed for this route
 */
export default function RestrictedRoute(props: RestrictedRouteProps) {
	const { redirect = RouterPathMapping.AccessDenied, roles } = props;
	const { getUser } = useAuth();
	const toast = useClaimsToast();
	const location = useLocation();
	const user = getUser();

	// Check if user is authenticated first
	if (!user) {
		// Store the current URL to redirect back after login
		const currentPath = location.pathname + location.search;
		if (currentPath !== "/login") {
			localStorage.setItem("lastVisitedPage", currentPath);
		}
		// Set flag for session expiration alert on login page
		localStorage.setItem("sessionExpired", "true");
		// Redirect to login if not authenticated
		return <Navigate to={RouterPathMapping.Login} replace />;
	}

	const userRoles = (user?.role?.split(",") as UserRole[]) ?? [];
	const hasRole = roles.some((role) => userRoles.includes(role ?? ""));

	// Display a warning toast message if access is denied
	if (!hasRole) {
		toast("Access Denied", "You do not have the correct permission to access this page", "warning");
	}

	// If user has the required role, render children. Otherwise redirect
	return hasRole ? <Outlet context={{ user }} /> : <Navigate to={redirect} />;
}
